import React, { useMemo, useState } from "react";
import FlatCard from "components/UI-lib/FlatCard";
import { AreaChart } from "../../../Graphs/AreaGraph";
import {
  classNames,
  formatGrantHeadline,
  inclusiveEndOfDay,
  monthNames,
} from "components/utils";
import {
  DateRangePicker,
  type DateRange,
} from "components/Graphs/DateRangePicker";
import { useQuery } from "@tanstack/react-query";
import agent from "server";
import { CurrencyDisplay } from "components/OrgPages/types";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import BillingSupportContactLink from "../BillingSupportContactLink";

const chartdata = [
  {
    date: "May 1",
    "Compute Costs": 2890,
    "Storage Costs": 2338,
  },
  {
    date: "May 2",
    "Compute Costs": 2756,
    "Storage Costs": 2103,
  },
  {
    date: "May 3",
    "Compute Costs": 3322,
    "Storage Costs": 2194,
  },
  {
    date: "May 4",
    "Compute Costs": 3470,
    "Storage Costs": 2108,
  },
  {
    date: "May 5",
    "Compute Costs": 3475,
    "Storage Costs": 1812,
  },
  {
    date: "May 6",
    "Compute Costs": 3129,
    "Storage Costs": 1726,
  },
  {
    date: "May 7",
    "Compute Costs": 3490,
    "Storage Costs": 1982,
  },
  {
    date: "May 8",
    "Compute Costs": 2903,
    "Storage Costs": 2012,
  },
  {
    date: "May 9",
    "Compute Costs": 2643,
    "Storage Costs": 2342,
  },
  {
    date: "May 10",
    "Compute Costs": 2837,
    "Storage Costs": 2473,
  },
  {
    date: "May 11",
    "Compute Costs": 2954,
    "Storage Costs": 3848,
  },
  {
    date: "May 12",
    "Compute Costs": 3239,
    "Storage Costs": 3736,
  },
  {
    date: "May 13",
    "Compute Costs": 2324,
    "Storage Costs": 1800,
  },
  {
    date: "May 14",
    "Compute Costs": 4343,
    "Storage Costs": 1920,
  },
  {
    date: "May 15",
    "Compute Costs": 3200,
    "Storage Costs": 1234,
  },
  {
    date: "May 16",
    "Compute Costs": 2303,
    "Storage Costs": 2100,
  },
  {
    date: "May 17",
    "Compute Costs": 3300,
    "Storage Costs": 2500,
  },
  {
    date: "May 18",
    "Compute Costs": 3230,
    "Storage Costs": 1800,
  },
  {
    date: "May 19",
    "Compute Costs": 3004,
    "Storage Costs": 1750,
  },
  {
    date: "May 20",
    "Compute Costs": 3600,
    "Storage Costs": 2200,
  },
  {
    date: "May 21",
    "Compute Costs": 2543,
    "Storage Costs": 2100,
  },
  {
    date: "May 22",
    "Compute Costs": 3232,
    "Storage Costs": 2000,
  },
  {
    date: "May 23",
    "Compute Costs": 2000,
    "Storage Costs": 1400,
  },
  {
    date: "May 24",
    "Compute Costs": 3234,
    "Storage Costs": 1234,
  },
  {
    date: "May 25",
    "Compute Costs": 2400,
    "Storage Costs": 1700,
  },
  {
    date: "May 26",
    "Compute Costs": 2405,
    "Storage Costs": 1600,
  },
  {
    date: "May 27",
    "Compute Costs": 4300,
    "Storage Costs": 1800,
  },
  {
    date: "May 28",
    "Compute Costs": 4400,
    "Storage Costs": 1400,
  },
  {
    date: "May 29",
    "Compute Costs": 2345,
    "Storage Costs": 1324,
  },
  {
    date: "May 30",
    "Compute Costs": 3245,
    "Storage Costs": 1200,
  },
];

interface BillingOverviewProps {
  activeOrgId: string;
  currencyDisplay?: CurrencyDisplay;
  showProgramEndDate?: boolean;
  programEndDateLabel?: string | null;
  programDaysRemaining?: number | null;
}

const BillingOverview: React.FC<BillingOverviewProps> = ({
  activeOrgId,
  currencyDisplay,
  showProgramEndDate = false,
  programEndDateLabel = null,
  programDaysRemaining = null,
}) => {
  const gpuHoursCategory = "Total Compute + Storage Hours";
  const shellPreference = useApplicationShellPreference();
  const showInlineSupportContact =
    isApplicationShellRedesigned(shellPreference);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: firstDay,
      to: now,
    };
  });

  const getUsageOverview = async (orgId: string) => {
    const from = dateRange?.from ?? new Date();
    const to = inclusiveEndOfDay(dateRange?.to ?? new Date());
    const res = await agent.Organizations.getUsageOverview(orgId, {
      from,
      to,
    });
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch usage overview");
    }
    return res.data.Usage || [];
  };

  const { data: usageOverview, isLoading: usageOverviewLoading } = useQuery({
    queryKey: ["usage-overview", activeOrgId, dateRange],
    queryFn: () => getUsageOverview(activeOrgId),
    enabled: !!activeOrgId && !!dateRange?.from && !!dateRange?.to,
  });

  const displayData = useMemo(() => {
    const dataLookup: {
      [key: string]: {
        ComputeCost: number;
        StorageCost: number;
        TotalTime: number;
        date: Date;
      };
    } = {};

    usageOverview?.forEach((item) => {
      const utcDate = new Date(item.Date);
      const dateString = utcDate.toUTCString().split(" ").slice(0, 4).join(" ");
      dataLookup[dateString] = {
        ComputeCost: Number(item.ComputeCost || 0),
        StorageCost: Number(item.StorageCost || 0),
        TotalTime: Number(item.TotalTime || 0),
        date: utcDate,
      };
    });

    return Object.values(dataLookup)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .map(({ ComputeCost, StorageCost, TotalTime, date }) => ({
        date: `${monthNames[date.getUTCMonth()]} ${date.getUTCDate()}`,
        "Compute Costs": ComputeCost,
        "Storage Costs": StorageCost,
        [gpuHoursCategory]: TotalTime,
      }));
  }, [usageOverview]);

  if (activeOrgId) {
    return (
      <>
        <FlatCard
          noBorder={true}
          noBackground
          className={showInlineSupportContact ? "pt-0" : undefined}
        >
          <div className="flex flex-col">
            {showInlineSupportContact && showProgramEndDate ? (
              <div className="mb-3 flex justify-end">
                <BillingSupportContactLink className="ml-auto text-right" />
              </div>
            ) : null}
            <div className="relative">
              <div
                className={
                  showProgramEndDate ? "pointer-events-none opacity-20" : ""
                }
              >
                <div
                  className={classNames(
                    "flex flex-row flex-wrap items-center gap-3",
                    showInlineSupportContact && !showProgramEndDate
                      ? "justify-between"
                      : "",
                  )}
                >
                  <div className="flex flex-row items-center gap-2">
                    <h1 className="text-xl font-medium dark:text-white">
                      Usage Overview for{" "}
                    </h1>
                    <DateRangePicker
                      className="w-60"
                      value={dateRange}
                      onChange={setDateRange}
                      requiredRange={true}
                    />
                  </div>
                  {showInlineSupportContact && !showProgramEndDate ? (
                    <BillingSupportContactLink className="ml-auto text-right" />
                  ) : null}
                </div>
                <div className="mt-2 min-h-80">
                  {usageOverviewLoading || displayData.length === 0 ? (
                    <div className="relative">
                      <div
                        className={classNames(
                          "absolute top-[20%] z-50",
                          usageOverviewLoading ? "left-[48%]" : "left-[40%]",
                        )}
                      >
                        <span className="text-white font-bold">
                          {usageOverviewLoading
                            ? "Loading..."
                            : "No Usage Data Available"}
                        </span>
                      </div>
                      <AreaChart
                        className="opacity-15 h-80"
                        data={chartdata}
                        showTooltip={false}
                        disableHoverDots={true}
                        index="date"
                        categories={["Compute Costs", "Storage Costs"]}
                        valueFormatter={(number: number) =>
                          `$${Intl.NumberFormat("us").format(number).toString()}`
                        }
                        intervalType="preserveStartEnd"
                      />
                    </div>
                  ) : currencyDisplay === "hours" ? (
                    <AreaChart
                      className="h-80"
                      data={displayData}
                      index="date"
                      categories={[gpuHoursCategory]}
                      valueFormatter={(number: number) =>
                        (number / 3600).toFixed(1)
                      }
                      intervalType="preserveStartEnd"
                    />
                  ) : (
                    <AreaChart
                      className="h-80"
                      data={displayData}
                      index="date"
                      categories={["Compute Costs", "Storage Costs"]}
                      valueFormatter={(number: number) =>
                        `$${Intl.NumberFormat("us").format(number).toString()}`
                      }
                      intervalType="preserveStartEnd"
                    />
                  )}
                </div>
              </div>
              {showProgramEndDate ? (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black bg-opacity-10 px-6">
                  <div className="flex max-w-xl flex-col items-center gap-2 text-center">
                    <p className="text-lg font-medium text-highlight">
                      {formatGrantHeadline(
                        programEndDateLabel,
                        programDaysRemaining,
                      )}
                    </p>
                    <p className="text-sm font-medium text-white">
                      Usage Overview is not available for Academic Grant. See instance usage in the list below.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </FlatCard>
      </>
    );
  }
  // when clicking the settings button, reponse time is so fast the seeing loading bars feels off
  return <div />;
};

export default BillingOverview;
